import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setManualReceiptsPayload,
  setReceipts,
} from "../../../../../features/financeReceiptSlice";

const ReceiptForm = ({
  receipt,
  receiptNumber,
  canDelete,
  onDelete,
  handleFieldChange,
  handleCompanyChange,
  handleAddFme,
  handleFmeChange,
  handleRemoveFme,
  companyOptions,
  currencyOptions,
  duplicateReceiptNumbers,
  totalReceipts,
}: any) => {
  const fmeRecords: string[] = receipt.fmeRecords ?? [];
  const companySearchValue = String(
    receipt.companySearch ?? receipt.company?.label ?? "",
  );

  const filteredCompanyOptions = companySearchValue
    ? (companyOptions || []).filter((option: any) =>
        option.label.toLowerCase().includes(companySearchValue.toLowerCase()),
      )
    : companyOptions || [];

  let title = "";

  if (totalReceipts === 1) {
    title = "Required Fields";
  } else {
    title = `Receipt #${receiptNumber}`;
  }
  return (
    <Grid
      container
      spacing={2}
      mb={2}
      pb={3}
      sx={{ borderBottom: "1px solid #ccc" }}
    >
      <Grid size={12}>
        <Typography
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {" "}
          <span>{title}</span>
          {canDelete && (
            <IconButton onClick={() => onDelete(receipt.id)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Typography>
      </Grid>

      <Grid size={4}>
        <TextField
          label="Receipt Number"
          value={receipt.receiptNumber}
          color="secondary"
          onChange={(e) =>
            handleFieldChange(receipt.id, "receiptNumber", e.target.value)
          }
          placeholder="The transaction serial number "
          fullWidth
          size="small"
          error={
            !!receipt.receiptNumber &&
            duplicateReceiptNumbers.has(receipt.receiptNumber)
          }
          helperText={
            duplicateReceiptNumbers.has(receipt.receiptNumber)
              ? "Receipt number must be unique"
              : ""
          }
        />
      </Grid>
      <Grid size={4}>
        <Autocomplete
          sx={{ width: "100%" }}
          options={filteredCompanyOptions}
          value={receipt.company}
          inputValue={companySearchValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input" || reason === "clear") {
              handleFieldChange(receipt.id, "companySearch", newInputValue);
            }
          }}
          filterOptions={(options) => options}
          onChange={(_, newValue) => handleCompanyChange(receipt.id, newValue)}
          isOptionEqualToValue={(option, value) =>
            option?.shared_uid === value?.shared_uid
          }
          getOptionLabel={(option) => option?.label ?? ""}
          renderOption={(props, option) => {
            const { key: _key, ...optionProps } = props;
            void _key;
            return (
              <li
                key={`${option.shared_uid}-${option.finance_company_id}`}
                {...optionProps}
              >
                {option.label}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Finance Company Name"
              placeholder="Who issued this receipt"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ ml: 1 }} />
                  </InputAdornment>
                ),
              }}
              color="secondary"
              size="small"
            />
          )}
        />
      </Grid>
      <Grid size={4}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={receipt.date}
            format="DD/MM/YYYY"
            onChange={(newValue) =>
              handleFieldChange(receipt.id, "date", newValue)
            }
            slotProps={{
              textField: {
                size: "small",
                color: "secondary",
                // required: true,
              },
            }}
            sx={{ width: "100%" }}
            label="Date"
          />
        </LocalizationProvider>
      </Grid>
      <Grid size={6}>
        <TextField
          label="Receipt Amount"
          value={receipt.receiptAmount}
          color="secondary"
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d*$/.test(value)) {
              handleFieldChange(receipt.id, "receiptAmount", value);
            }
          }}
          placeholder="The sum listed on the receipt"
          fullWidth
          size="small"
          inputProps={{
            inputMode: "decimal",
            pattern: "[0-9.]*",
          }}
        />
      </Grid>
      <Grid size={6}>
        <Autocomplete
          sx={{ width: "100%" }}
          size="small"
          options={currencyOptions || []}
          value={receipt.currency}
          isOptionEqualToValue={
            (option, value) => option?.shared_uid === value?.shared_uid // Add this
          }
          onChange={(_, newValue) =>
            handleFieldChange(receipt.id, "currency", newValue)
          }
          getOptionLabel={(option) => `${option.symbol} ${option.code}`}
          renderOption={(props, option) => (
            <li {...props}>
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 500,
                  marginRight: "8px",
                }}
              >
                {option.symbol}
              </span>
              {option.code}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Currency"
              color="secondary"
              placeholder="Choose from the list"
            />
          )}
        />
      </Grid>

      <Grid size={12}>
        <Typography sx={{ mb: 1 }}>FME</Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {fmeRecords.map((value: string, index: number) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                value={value}
                color="secondary"
                size="small"
                onChange={(e) => {
                  const next = e.target.value;
                  if (/^\d*$/.test(next)) {
                    handleFmeChange(receipt.id, index, next);
                  }
                }}
                error={value.trim() === ""}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  width: 96,
                  "& .MuiOutlinedInput-root": {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              />
              <IconButton
                aria-label="Remove FME"
                onClick={() => handleRemoveFme(receipt.id, index)}
                sx={{
                  width: 40,
                  height: 40,
                  border: "1px solid rgba(0, 0, 0, 0.23)",
                  borderLeft: 0,
                  borderRadius: "0 4px 4px 0",
                  color: "rgba(0, 0, 0, 0.54)",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="medium"
            endIcon={<AddIcon />}
            onClick={() => handleAddFme(receipt.id)}
            sx={{
              color: "rgba(0, 0, 0, 0.7)",
              borderColor: "rgba(0, 0, 0, 0.23)",
              "&:hover": {
                borderColor: "rgba(0, 0, 0, 0.4)",
                backgroundColor: "transparent",
              },
            }}
          >
            ADD FME
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

const AddReceiptsManualInput = () => {
  const dispatch = useDispatch();

  const { allCurrencies, allCompanies, receipts } = useSelector(
    (state: any) => state.receipts,
  );
  const companyOptions = useMemo(
    () =>
      allCompanies
        ?.filter(
          (company: any) =>
            company?.finance_company_name?.trim() &&
            company?.finance_company_id != null,
        )
        .map((company: any) => ({
          label: company.finance_company_name.trim(),
          shared_uid: company.shared_uid,
          finance_company_id: company.finance_company_id,
        })) || [],
    [allCompanies],
  );

  const currencyOptions = useMemo(
    () =>
      allCurrencies?.map((currency: any) => ({
        code: currency.currency_code,
        symbol: currency.currency_symbol,
        shared_uid: currency.shared_uid,
      })),
    [allCurrencies],
  );

  const handleAddReceipt = () => {
    const newId =
      receipts.length > 0 ? Math.max(...receipts.map((r: any) => r.id)) + 1 : 1;
    dispatch(
      setReceipts([
        ...receipts,
        {
          id: newId,
          receiptNumber: "",
          receiptAmount: "",
          company: null,
          currency: null,
          date: null,
          companySearch: "",
          fmeRecords: [],
        },
      ]),
    );
  };
  const handleDeleteReceipt = (id: any) => {
    if (receipts.length > 1) {
      dispatch(
        setReceipts(receipts.filter((receipt: any) => receipt.id !== id)),
      );
    }
  };
  const handleFieldChange = (id: any, field: any, value: any) => {
    dispatch(
      setReceipts(
        receipts.map((receipt: any) =>
          receipt.id === id ? { ...receipt, [field]: value } : receipt,
        ),
      ),
    );
  };
  const handleCompanyChange = (id: any, company: any) => {
    dispatch(
      setReceipts(
        receipts.map((receipt: any) =>
          receipt.id === id
            ? {
                ...receipt,
                company,
                companySearch: company?.label ?? "",
              }
            : receipt,
        ),
      ),
    );
  };
  const handleAddFme = (id: any) => {
    dispatch(
      setReceipts(
        receipts.map((receipt: any) =>
          receipt.id === id
            ? { ...receipt, fmeRecords: [...(receipt.fmeRecords ?? []), ""] }
            : receipt,
        ),
      ),
    );
  };
  const handleFmeChange = (id: any, index: number, value: string) => {
    dispatch(
      setReceipts(
        receipts.map((receipt: any) =>
          receipt.id === id
            ? {
                ...receipt,
                fmeRecords: (receipt.fmeRecords ?? []).map(
                  (v: string, i: number) => (i === index ? value : v),
                ),
              }
            : receipt,
        ),
      ),
    );
  };
  const handleRemoveFme = (id: any, index: number) => {
    dispatch(
      setReceipts(
        receipts.map((receipt: any) =>
          receipt.id === id
            ? {
                ...receipt,
                fmeRecords: (receipt.fmeRecords ?? []).filter(
                  (_: string, i: number) => i !== index,
                ),
              }
            : receipt,
        ),
      ),
    );
  };
  const buildPayload = () => {
    return receipts.map((receipt: any) => {
      const fmeRecords = (receipt.fmeRecords ?? []).map((v: string) => {
        const trimmed = String(v).trim();
        // Keep empties as "" so validation still flags incomplete FME fields;
        // valid entries are sent as integers.
        return trimmed === "" ? "" : Number(trimmed);
      });
      return {
        receipt_id: receipt.receiptNumber,
        receipt_date: receipt.date ? receipt.date.format("YYYY-MM-DD") : null,
        receipt_amount: receipt.receiptAmount
          ? Number(receipt.receiptAmount)
          : null,
        receipt_currency_shared_uid: receipt.currency?.shared_uid ?? null,
        company_shared_uid: receipt.company?.shared_uid ?? null,
        fme_records: fmeRecords.length > 0 ? fmeRecords : null,
      };
    });
  };

  const duplicateReceiptNumbers = useMemo(() => {
    const counts: Record<string, number> = {};

    receipts.forEach((r: any) => {
      const value = r.receiptNumber?.trim();
      if (!value) return;
      counts[value] = (counts[value] || 0) + 1;
    });

    return new Set(Object.keys(counts).filter((key) => counts[key] > 1));
  }, [receipts]);

  useEffect(() => {
    const payload = buildPayload();
    dispatch(setManualReceiptsPayload(payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts, dispatch]);

  return (
    <>
      {receipts.map((receipt: any, index: any) => (
        <ReceiptForm
          key={receipt.id}
          receipt={receipt}
          receiptNumber={index + 1}
          totalReceipts={receipts.length}
          canDelete={receipts.length > 1}
          onDelete={handleDeleteReceipt}
          handleFieldChange={handleFieldChange}
          handleCompanyChange={handleCompanyChange}
          handleAddFme={handleAddFme}
          handleFmeChange={handleFmeChange}
          handleRemoveFme={handleRemoveFme}
          companyOptions={companyOptions}
          currencyOptions={currencyOptions}
          duplicateReceiptNumbers={duplicateReceiptNumbers}
        />
      ))}

      <Grid container spacing={2} mt={3}>
        <Grid size={12}>
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            endIcon={<AddIcon sx={{ ml: "0.5" }} />}
            onClick={handleAddReceipt}
          >
            Add another
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default AddReceiptsManualInput;
